import React, { useMemo } from 'react'
import { Autocomplete, Box, ButtonBase, MenuItem, TextField, Typography } from '@mui/material'
import palette from '../../../../theme/palette'
import {
  ATTRIBUTABLE, OWNERS, PENDING_WITH,
  SUB_TEAM_LABEL, TEAM_LABEL, TEAM_OF, canAct, defaultPendingWith,
} from '../constants'
import { FormField, inputSx } from '../eddBreachUi'

/**
 * The one attribution block used by Attribute / Update, the KAM Reply and Close (spec §9.2).
 * mode: 'attribute' | 'reply' | 'close'. Controlled: `value` + `onChange(patch)`.
 * Every rule here is also enforced by the backend; this only gives instant feedback.
 * Layout follows the artifact: labels above fields, 40px inputs, three equal owner cards.
 */

export const emptyForm = {
  reasonCode: null, replyCode: null, owner: '',
  overrideRemark: '', pendingWith: '', remarks: '', assigneeMemberId: '',
}

/** The owner a reason (or, at closure, the hold split) suggests; picking another one is an override. */
export function isOverridden(mode, value, reasons, suggestedFinalOwner) {
  const r = reasons.find((x) => x.code === value.reasonCode)
  if (!r || !value.owner) return false
  if (value.owner === r.owner) return false
  return !(mode === 'close' && value.owner === suggestedFinalOwner)
}

/** Length limits from GET masters (`limits`); empty until masters has loaded, when nothing can be saved anyway. */
export const limitsOf = (masters) => masters?.limits || {}

/** Client-side mirror of the server rules (ctx.limits = masters.limits). Returns { field: message }. */
export function validate(mode, value, ctx) {
  const e = {}
  const { minReplyRemarks = 0, minOverrideRemark = 0 } = ctx.limits || {}
  if (mode === 'reply') {
    if (!value.replyCode) e.replyCode = 'Select what you did, so operations can see the client side is cleared.'
    if ((value.remarks || '').trim().length < minReplyRemarks) e.remarks = `Tell operations where it is and what to do next (at least ${minReplyRemarks} characters).`
    if (!value.assigneeMemberId) e.assigneeMemberId = 'Select the PTL Operations member this goes back to.'
    return e
  }
  if (!value.reasonCode) e.reasonCode = 'Pick the reason for this delay.'
  if (!value.owner) e.owner = 'Select Prozo, Client or External.'
  if (isOverridden(mode, value, ctx.reasons, ctx.suggestedFinalOwner) && (value.overrideRemark || '').trim().length < minOverrideRemark) {
    e.overrideRemark = `You changed the suggested attribution. Say why, in at least ${minOverrideRemark} characters.`
  }
  if (mode === 'attribute') {
    if (!value.pendingWith) e.pendingWith = 'Select who holds the next action.'
    else if (value.pendingWith === 'CLIENT' && value.owner !== 'CLIENT') e.pendingWith = 'Pending with Client only applies to a client attributable delay.'
    if (!value.assigneeMemberId) e.assigneeMemberId = 'Assign this to a member of the routed team.'
  }
  if (!(value.remarks || '').trim()) e.remarks = 'Remarks are mandatory. Record what was done or what is needed.'
  return e
}

/** Pre-select the KAM mapped to this merchant when the delay is the client's. */
export function mappedKam(row, masters) {
  const m = (masters?.kamMapping || []).find((x) => x.merchantOid === row?.merchantOid)
  const kam = m && (masters?.team || []).find((t) => t.id === m.kamMemberId && t.active && canAct(t, 'KAM'))
  return kam ? kam.id : ''
}

/** Apply a reason pick: owner, pending-with and assignee follow it (spec §9.3). No root cause (artifact 06-Oct-2026). */
export function pickReason(mode, value, reason, row, masters, suggestedFinalOwner) {
  if (!reason) return { ...value, reasonCode: null }
  let owner = reason.owner
  if (mode === 'close' && suggestedFinalOwner && suggestedFinalOwner !== reason.owner) owner = suggestedFinalOwner
  const patch = { ...value, reasonCode: reason.code, owner }
  if (mode === 'attribute') {
    patch.pendingWith = defaultPendingWith(owner)
    patch.assigneeMemberId = keepOrMap(value.assigneeMemberId, owner, row, masters)
  }
  return patch
}

function keepOrMap(currentId, owner, row, masters) {
  const team = TEAM_OF[owner]
  const cur = (masters?.team || []).find((t) => t.id === currentId)
  if (cur && cur.active && canAct(cur, team)) return currentId
  return team === 'KAM' ? mappedKam(row, masters) : ''
}

/** Tinted one-line note, like the artifact's `.route` strip. */
const noteSx = (tone) => ({ fontSize: 11.5, fontWeight: 600, lineHeight: 1.5, p: '7px 11px', borderRadius: '6px', bgcolor: tone.lighter, color: tone.darker || tone.dark })

const cardSx = (on, color, bg) => ({
  display: 'block', width: '100%', textAlign: 'center', p: '10px 8px', borderRadius: '6px', bgcolor: on ? bg : 'background.paper',
  border: `2px solid ${on ? color : palette.grey[300]}`, '&:hover': { borderColor: on ? color : palette.grey[500] },
})

export default function AttributionForm({ mode, row, masters, value, onChange, errors = {}, suggestedFinalOwner, disabled }) {
  const reasons = useMemo(() => (masters?.reasons || []), [masters])
  const team = masters?.team || []
  const { maxRemarks, minOverrideRemark, maxOverrideRemark } = limitsOf(masters)
  const set = (patch) => onChange({ ...value, ...patch })
  const reason = reasons.find((r) => r.code === value.reasonCode)
  const overridden = isOverridden(mode, value, reasons, suggestedFinalOwner)

  const reasonOptions = useMemo(() => reasons.filter((r) => !r.retired || r.code === value.reasonCode)
    .slice().sort((a, b) => ATTRIBUTABLE.indexOf(a.owner) - ATTRIBUTABLE.indexOf(b.owner) || (a.displayOrder ?? 999) - (b.displayOrder ?? 999) || a.label.localeCompare(b.label)), [reasons, value.reasonCode])
  // the replies that answer what operations asked for (reason_master linkedCodes), plus those that fit any request
  const replyOptions = useMemo(() => (masters?.replies || [])
    .filter((r) => !r.retired && (r.answersAny || (r.answers || []).includes(row?.reasonCode))), [masters, row])

  const setOwner = (owner) => {
    if (!owner || disabled) return
    const patch = { owner }
    if (mode === 'attribute') {
      patch.pendingWith = owner === 'CLIENT' ? 'CLIENT' : (value.pendingWith === 'CLIENT' ? 'PTL_OPS' : value.pendingWith || 'PTL_OPS')
      patch.assigneeMemberId = keepOrMap(value.assigneeMemberId, owner, row, masters)
    }
    set(patch)
  }

  const routedTeam = value.owner ? TEAM_OF[value.owner] : null
  const assigneeTeam = mode === 'reply' ? 'PTL_OPS' : routedTeam
  const assignees = team.filter((t) => t.active && canAct(t, assigneeTeam))
    .sort((a, b) => (a.subTeam || '').localeCompare(b.subTeam || '') || a.name.localeCompare(b.name))
  const kamId = mappedKam(row, masters)
  const kamName = team.find((t) => t.id === kamId)?.name

  const remarksBox = (placeholder) => (
    <FormField label='Remarks' required error={errors.remarks} hint={maxRemarks ? `${value.remarks.length}/${maxRemarks}` : ''}>
      <TextField fullWidth multiline minRows={3} size='small' sx={inputSx} disabled={disabled} placeholder={placeholder}
        value={value.remarks} onChange={(e) => set({ remarks: e.target.value })} inputProps={{ maxLength: maxRemarks }} error={!!errors.remarks} />
    </FormField>
  )

  if (mode === 'reply') {
    const request = reasons.find((r) => r.code === row?.reasonCode)
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={noteSx(palette.primary)}>
          Operations raised this as <b>{request?.label}</b>, waiting on {request?.closesWith}. That stays on the record. Pick what you did
          and name the operations member it goes back to. From now the shipment is Prozo attributable; the days the client held it stay against the client.
        </Box>
        <FormField label='What you did' required error={errors.replyCode}>
          <TextField select fullWidth size='small' sx={inputSx} disabled={disabled} value={value.replyCode || ''}
            onChange={(e) => set({ replyCode: e.target.value })} error={!!errors.replyCode} SelectProps={{ displayEmpty: true }}>
            <MenuItem value='' disabled sx={{ fontSize: 13 }}>Select what you did</MenuItem>
            {replyOptions.map((r) => <MenuItem key={r.code} value={r.code} sx={{ fontSize: 13 }}>{r.label}</MenuItem>)}
          </TextField>
        </FormField>
        {remarksBox('What operations should do next. For example: invoice uploaded on ProShip against this AWB, please re-plan the delivery.')}
        <AssigneeSelect label='Hand back to' value={value.assigneeMemberId} options={assignees} disabled={disabled}
          onChange={(id) => set({ assigneeMemberId: id })} error={errors.assigneeMemberId}
          hint='PTL Operations only, pre-filled with whoever raised the request.' />
      </Box>
    )
  }

  const tone = value.owner === 'CLIENT' ? palette.warning : palette.primary

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormField label='Reason for delay' required error={errors.reasonCode}
        hint={reason ? (reason.closesWith ? `Closes when we have: ${reason.closesWith}.` : '') : 'Type to search the reasons (Tools → Reason Master).'}>
        <Autocomplete
          size='small'
          options={reasonOptions}
          groupBy={(r) => OWNERS[r.owner]?.label}
          getOptionLabel={(r) => r.label}
          isOptionEqualToValue={(a, b) => a.code === b.code}
          value={reason || null}
          disabled={disabled}
          onChange={(_e, r) => onChange(pickReason(mode, value, r, row, masters, suggestedFinalOwner))}
          ListboxProps={{ sx: { fontSize: 13 } }}
          renderInput={(p) => <TextField {...p} sx={inputSx} placeholder='Select the reason for this delay' error={!!errors.reasonCode} />}
        />
      </FormField>

      <FormField label='Attributable to' required error={errors.owner}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          {ATTRIBUTABLE.map((o) => {
            const on = value.owner === o
            return (
              <ButtonBase key={o} onClick={() => setOwner(o)} disabled={disabled} aria-pressed={on} sx={cardSx(on, OWNERS[o].color, OWNERS[o].bg)}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, color: on ? OWNERS[o].color : 'text.primary' }}>{OWNERS[o].label}</Typography>
                <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.4, lineHeight: 1.35 }}>{OWNERS[o].desc}</Typography>
              </ButtonBase>
            )
          })}
        </Box>
        {/* closing ends the escalation here, so there is no "Assign to" note at closure (PTL team, 08-Oct-2026) */}
        {routedTeam && mode !== 'close' && (
          <Box sx={{ ...noteSx(tone), mt: 1 }}>
            Assign to a <b>{TEAM_LABEL[routedTeam]}</b>. {value.owner === 'CLIENT' ? 'The KAM chases the client.' : 'PTL Operations drives it with the carrier or the warehouse.'}
          </Box>
        )}
        {mode === 'close' && suggestedFinalOwner && value.owner === suggestedFinalOwner && reason && reason.owner !== suggestedFinalOwner && (
          <Box sx={{ ...noteSx(tone), mt: 1 }}>
            Suggested from the hold split: {OWNERS[suggestedFinalOwner].short} held this shipment longest. Change it if the record says otherwise.
          </Box>
        )}
      </FormField>

      {overridden && (
        <Box sx={{ p: '11px 13px', borderRadius: '6px', border: `1px solid ${palette.warning.light}`, bgcolor: palette.warning.lighter, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: palette.warning.darker, textTransform: 'uppercase', letterSpacing: '0.4px' }}>You changed the suggested attribution</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            The reason you picked suggests <b>{OWNERS[reason?.owner]?.label}</b>. You selected <b>{OWNERS[value.owner]?.label}</b>.
          </Typography>
          <TextField fullWidth multiline minRows={2} size='small' sx={inputSx} disabled={disabled}
            placeholder={`Why does this delay belong to the party you picked?${minOverrideRemark ? ` Minimum ${minOverrideRemark} characters.` : ''}`}
            value={value.overrideRemark} onChange={(e) => set({ overrideRemark: e.target.value })} inputProps={{ maxLength: maxOverrideRemark }}
            error={!!errors.overrideRemark} helperText={errors.overrideRemark} />
        </Box>
      )}

      {mode === 'attribute' && (
        <FormField label='Pending with' required error={errors.pendingWith} hint='Who holds the next action. This is not the same as who the delay belongs to.'>
          <TextField select fullWidth size='small' sx={inputSx} disabled={disabled}
            value={value.pendingWith} onChange={(e) => set({ pendingWith: e.target.value })} error={!!errors.pendingWith}>
            {PENDING_WITH.map((p) => <MenuItem key={p.value} value={p.value} sx={{ fontSize: 13 }} disabled={p.value === 'CLIENT' && value.owner !== 'CLIENT'}>{p.label}</MenuItem>)}
          </TextField>
        </FormField>
      )}

      {remarksBox(mode === 'close' ? 'Final note for the record: what caused the delay and what was done about it.' : 'What did you do, and what is now needed from the next owner?')}

      {mode === 'attribute' && (
        <AssigneeSelect label='Assign to' value={value.assigneeMemberId} options={assignees} disabled={disabled || !routedTeam}
          onChange={(id) => set({ assigneeMemberId: id })} error={errors.assigneeMemberId}
          hint={!routedTeam ? 'Pick a reason first.' : routedTeam === 'KAM'
            ? `Key Account Managers only.${kamName ? ` ${kamName} is mapped to ${row?.merchantName}.` : ''}`
            : 'PTL Operations only, grouped into Core Operations and First Time Right.'} />
      )}
    </Box>
  )
}

function AssigneeSelect({ label, value, options, onChange, error, hint, disabled }) {
  return (
    <FormField label={label} required error={error} hint={hint}>
      <TextField select fullWidth size='small' sx={inputSx} disabled={disabled} SelectProps={{ displayEmpty: true }}
        value={options.some((o) => o.id === value) ? value : ''} onChange={(e) => onChange(e.target.value)} error={!!error}>
        <MenuItem value='' disabled sx={{ fontSize: 13 }}>{options.length ? 'Select a member' : 'Pick a reason first'}</MenuItem>
        {options.map((m) => (
          <MenuItem key={m.id} value={m.id} sx={{ fontSize: 13 }}>
            {m.name}
            <Typography component='span' sx={{ ml: 1, fontSize: 11, color: 'text.secondary' }}>{m.subTeam ? SUB_TEAM_LABEL[m.subTeam] : m.designation}</Typography>
          </MenuItem>
        ))}
      </TextField>
    </FormField>
  )
}
